import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function eventLabel(item) {
  return `${item.title} · ${item.date}`;
}

function HomeSection({ title, open, onToggle, children }) {
  return (
    <section className={`home-admin-card${open ? " is-open" : ""}`}>
      <h3>
        <button type="button" aria-expanded={open} onClick={onToggle}>
          {title}
          <span className="home-admin-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="accordion-fold">
        <div className="accordion-panel" inert={!open}>
          <div className="home-admin-panel">{children}</div>
        </div>
      </div>
    </section>
  );
}

function AdminHome({ token }) {
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [events, setEvents] = useState([]);
  const [saturdayEvent, setSaturdayEvent] = useState("");
  const [sundayEvent, setSundayEvent] = useState("");
  const [savingEvent, setSavingEvent] = useState(false);
  const [openSection, setOpenSection] = useState("lawn");
  const [addOpen, setAddOpen] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  useEffect(() => {
    const config = authConfig(token);
    axios
      .get("/api/admin/lawn-photos/", config)
      .then(({ data }) => setPhotos(data))
      .catch((err) => setError(errorText(err)));
    axios
      .get("/api/admin/events/", config)
      .then(({ data }) => setEvents(data))
      .catch((err) => setError(errorText(err)));
    axios
      .get("/api/admin/home/", config)
      .then(({ data }) => {
        setSaturdayEvent(data.saturday ? String(data.saturday) : "");
        setSundayEvent(data.sunday ? String(data.sunday) : "");
      })
      .catch((err) => setError(errorText(err)));
  }, [token]);

  const saveTwoDays = async (event) => {
    event.preventDefault();
    setSavingEvent(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axios.put(
        "/api/admin/home/",
        { saturday: saturdayEvent || null, sunday: sundayEvent || null },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setSaturdayEvent(data.saturday ? String(data.saturday) : "");
      setSundayEvent(data.sunday ? String(data.sunday) : "");
      setNotice(
        data.saturday || data.sunday ? "Two days saved." : "Two days will show Event coming soon."
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSavingEvent(false);
    }
  };

  const upload = async (event) => {
    event.preventDefault();
    if (!files.length) {
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    const formData = new FormData();
    files.forEach((file) => formData.append("photos", file));
    try {
      const { data } = await axios.post("/api/admin/lawn-photos/", formData, authConfig(token));
      const added = Array.isArray(data) ? data : data.photos || [];
      setPhotos((current) => [...current, ...added]);
      setFiles([]);
      setFileKey((value) => value + 1);
      setAddOpen(false);
      setNotice(data.detail || "Photos added.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photo) => {
    if (!(await ask(removePrompt("this lawn photo")))) {
      return;
    }
    setError("");
    setNotice("");
    try {
      await axios.delete(`/api/admin/lawn-photos/${photo.id}/`, authConfig(token));
      setPhotos((current) => current.filter((item) => item.id !== photo.id));
    } catch (err) {
      setError(errorText(err));
    }
  };

  const eventOptions = () => (
    <>
      <option value="">No event</option>
      {events.map((item) => (
        <option key={item.id} value={item.id}>
          {eventLabel(item)}
        </option>
      ))}
    </>
  );

  return (
    <>
      {confirmDialog}
      <h2>Home page</h2>
      {error ? <Message variant="danger">{error}</Message> : null}
      {notice ? <Message variant="info">{notice}</Message> : null}
      <HomeSection
        title="On the lawn"
        open={openSection === "lawn"}
        onToggle={() => setOpenSection((current) => (current === "lawn" ? "" : "lawn"))}
      >
        <p>
          Photos for On the lawn at the library. They fade on the home page. A click on a square shows that photo.
          Until a photo is added here, the page keeps the Central Park picture already in place.
        </p>
        <AdminAddButton onClick={() => setAddOpen(true)}>Add photos</AdminAddButton>
        <AdminFormModal show={addOpen} title="Add lawn photos" onHide={() => setAddOpen(false)}>
        <Form onSubmit={upload}>
          <Form.Group className="mb-3" controlId="lawnPhotos">
            <Form.Label>Photos</Form.Label>
            <Form.Control
              key={fileKey}
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </Form.Group>
          <AdminFormActions
            saveLabel={busy ? "Uploading" : "Add photos"}
            busy={busy}
            disabled={files.length === 0}
            onCancel={() => setAddOpen(false)}
          />
        </Form>
        </AdminFormModal>
        {photos.length === 0 ? (
          <p className="mb-0 mt-3">No lawn photos yet.</p>
        ) : (
          <div className="admin-photo-grid">
            {photos.map((photo) => (
              <article key={photo.id} className="admin-photo-card">
                <img src={photo.thumb_url} alt="On the lawn" />
                <RecordActions name="this lawn photo" onRemove={() => remove(photo)} />
              </article>
            ))}
          </div>
        )}
      </HomeSection>
      <HomeSection
        title="Two days"
        open={openSection === "days"}
        onToggle={() => setOpenSection((current) => (current === "days" ? "" : "days"))}
      >
        <p className="text-muted">
          Saturday and Sunday are separate events. Saturday is its own morning. Sunday is the judging contest.
          Leave a day empty and that card keeps the plain picture and says Event coming soon.
        </p>
        <Form onSubmit={saveTwoDays}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="saturdayEvent">
                <Form.Label>Saturday</Form.Label>
                <Form.Select value={saturdayEvent} onChange={(e) => setSaturdayEvent(e.target.value)}>
                  {eventOptions()}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="sundayEvent">
                <Form.Label>Sunday</Form.Label>
                <Form.Select value={sundayEvent} onChange={(e) => setSundayEvent(e.target.value)}>
                  {eventOptions()}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Button type="submit" variant="primary" disabled={savingEvent}>
            {savingEvent ? "Saving" : "Save days"}
          </Button>
        </Form>
      </HomeSection>
    </>
  );
}

export default AdminHome;
